/**
 * Phase D5 (docs/feature_upgrade_plan.md) — frontend half of the "Capture
 * Event" diagnostics system. A pure module: no React, no `fetch` — mirrors
 * lib/sseClient.ts's reasoning for staying framework-free, so it's
 * unit-testable with plain function calls and no browser test harness
 * beyond jsdom's `window`/`console`.
 *
 * Inert until `setEnabled(true)` is called (api/client.ts's AuthGate does
 * this once, right after confirming login, based on GET /api/debug/status —
 * never guess the flag client-side). Every record/patch function below is a
 * no-op while disabled, so an ordinary deployment (DEBUG_CAPTURE unset)
 * pays no cost beyond the disabled check itself.
 *
 * ## Every buffered entry is chat-scoped, and `snapshot()` REQUIRES a chat id
 *
 * These buffers live for the whole page session and span every chat the user
 * visits. The backend half is already keyed by chat_id, but this half used to
 * be four flat module-level arrays that `snapshot()` returned wholesale — so a
 * bundle exported from chat B carried chat A's network requests and, worse,
 * chat A's SSE frames, which contain the streamed answer text and source
 * metadata. A capture bundle is made to be handed to someone else, so that is
 * a disclosure bug, not just untidiness.
 *
 * Fix: every record carries the chat it belongs to, and `snapshot(chatId)`
 * takes a REQUIRED chat id and returns only matching entries. Entries captured
 * outside any chat (login, settings) are deliberately dropped rather than
 * treated as "safe to include everywhere" — they are still this user's data
 * and are not evidence about the chat being reported. Attribution prefers an
 * explicit chat id from the caller (the stream knows which chat it is for,
 * even if the user has since navigated away) and falls back to the chat
 * currently on screen.
 */

interface ChatScoped {
  /** The chat this entry belongs to; null when captured outside any chat. */
  chat_id: string | null;
}

export interface NetworkRecord extends ChatScoped {
  id: string;
  method: string;
  url: string;
  status?: number;
  ok?: boolean;
  duration_ms: number;
  request_id: string;
  ts: number;
  error?: string;
}

export interface SSEFrameRecord extends ChatScoped {
  ts: number;
  request_id: string;
  event: unknown;
}

export interface ConsoleRecord extends ChatScoped {
  ts: number;
  level: "error" | "warn";
  args: string[];
}

export interface UIErrorRecord extends ChatScoped {
  ts: number;
  source: string;
  message: string;
  stack?: string;
}

const NETWORK_CAP = 200;
const SSE_CAP = 1000;
const CONSOLE_CAP = 500;
const UI_ERROR_CAP = 200;

let enabled = false;
let handlersInstalled = false;
/** The chat currently on screen — the fallback attribution for anything the
 * caller cannot tag explicitly (console output, window.onerror). */
let currentChatId: string | null = null;
let originalConsoleError: typeof console.error | null = null;
let originalConsoleWarn: typeof console.warn | null = null;

const network: NetworkRecord[] = [];
const sse: SSEFrameRecord[] = [];
const consoleEntries: ConsoleRecord[] = [];
const uiErrors: UIErrorRecord[] = [];

function pushCapped<T>(arr: T[], item: T, cap: number): void {
  arr.push(item);
  if (arr.length > cap) arr.splice(0, arr.length - cap);
}

function safeStringify(value: unknown): string {
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.stack || value.message;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** A fresh id per outbound request (Phase D1) — used both as the
 * `X-Request-ID` header value and to correlate a network record with its
 * SSE frames in the exported capture. */
export function newRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isEnabled(): boolean {
  return enabled;
}

/** Called by ChatView whenever the open chat changes, including to null when
 * no chat is open. Attribution fallback only — a caller that knows its own
 * chat id should pass it explicitly. */
export function setCurrentChat(chatId: string | null): void {
  currentChatId = chatId;
}

/** Chat-scoped API URLs carry the id in the path. Reading it there attributes
 * a request to the chat it actually targets rather than the one on screen,
 * which matters when a stream outlives the user navigating away. */
export function chatIdFromUrl(url: string): string | null {
  return /\/chats\/([0-9a-fA-F-]{36})\b/.exec(url)?.[1] ?? null;
}

/** Called once, post-login, from GET /api/debug/status's result. Installing
 * the console/window patches only happens on the transition into the
 * enabled state, so calling this repeatedly with the same value is safe. */
export function setEnabled(value: boolean): void {
  enabled = value;
  if (value) installGlobalHandlers();
}

function installGlobalHandlers(): void {
  if (handlersInstalled || typeof window === "undefined") return;
  handlersInstalled = true;

  originalConsoleError = console.error.bind(console);
  originalConsoleWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    pushCapped(consoleEntries, { ts: Date.now(), level: "error", args: args.map(safeStringify), chat_id: currentChatId }, CONSOLE_CAP);
    originalConsoleError?.(...args);
  };
  console.warn = (...args: unknown[]) => {
    pushCapped(consoleEntries, { ts: Date.now(), level: "warn", args: args.map(safeStringify), chat_id: currentChatId }, CONSOLE_CAP);
    originalConsoleWarn?.(...args);
  };

  window.addEventListener("error", (e: ErrorEvent) => {
    recordUIError("window.onerror", e.message, e.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason = e.reason as { message?: string; stack?: string } | undefined;
    recordUIError("unhandledrejection", String(reason?.message ?? e.reason), reason?.stack);
  });
}

export function recordNetwork(rec: Omit<NetworkRecord, "chat_id"> & { chat_id?: string | null }): void {
  if (!enabled) return;
  const chat_id = rec.chat_id ?? chatIdFromUrl(rec.url) ?? currentChatId;
  pushCapped(network, { ...rec, chat_id }, NETWORK_CAP);
}

export function recordSSEFrame(request_id: string, event: unknown, chatId?: string | null): void {
  if (!enabled) return;
  pushCapped(sse, { ts: Date.now(), request_id, event, chat_id: chatId ?? currentChatId }, SSE_CAP);
}

/** Fed explicitly by ErrorBoundary.componentDidCatch and the ChatView
 * stream-error setters — a render crash or a stream error the app already
 * displays to the user is exactly the kind of thing a capture bundle needs,
 * on top of whatever window.onerror/unhandledrejection catch on their own. */
export function recordUIError(source: string, message: string, stack?: string): void {
  if (!enabled) return;
  pushCapped(uiErrors, { ts: Date.now(), source, message, stack, chat_id: currentChatId }, UI_ERROR_CAP);
}

export interface CaptureSnapshot {
  network: NetworkRecord[];
  sse: SSEFrameRecord[];
  console: ConsoleRecord[];
  errors: UIErrorRecord[];
}

/**
 * The frontend half of a capture bundle for exactly one chat.
 *
 * `chatId` is required on purpose: there is no caller that legitimately wants
 * every chat's traffic, and making it optional is what allowed the disclosure
 * bug this signature exists to prevent.
 */
export function snapshot(chatId: string): CaptureSnapshot {
  const mine = <T extends ChatScoped>(rows: T[]): T[] => rows.filter(r => r.chat_id === chatId);
  return {
    network: mine(network),
    sse: mine(sse),
    console: mine(consoleEntries),
    errors: mine(uiErrors),
  };
}

/** Test-only reset — production code never needs to clear these buffers
 * mid-session; a fresh page load is the only reset that matters there. */
export function _resetForTests(): void {
  network.length = 0;
  sse.length = 0;
  consoleEntries.length = 0;
  uiErrors.length = 0;
  enabled = false;
  handlersInstalled = false;
  currentChatId = null;
  if (originalConsoleError) console.error = originalConsoleError;
  if (originalConsoleWarn) console.warn = originalConsoleWarn;
  originalConsoleError = null;
  originalConsoleWarn = null;
}
